import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, ArrowRight, ExternalLink } from "lucide-react";
import { api, API } from "../api";
import IMAGES from "../data/imageMap";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import CtaBanner from "../components/CtaBanner";
import Markdown from "../components/Markdown";
import ShareRow from "../components/ShareRow";
import NotFound from "./NotFound";

const SECTION_DISPLAY = {
  featured: "Featured",
  domains: "Domain",
  breakthroughs: "Breakthrough",
  impact: "Impact story",
  resources: "Resource",
};

const SECTION_PLURAL = {
  featured: "More featured",
  domains: "Other domains",
  breakthroughs: "More breakthroughs",
  impact: "More impact stories",
  resources: "More resources",
};

const SECTION_ANCHOR = {
  featured: "#top",
  domains: "#domains",
  breakthroughs: "#breakthroughs",
  impact: "#impact",
  resources: "#resources",
};

function resolveImage(card) {
  if (!card) return "";
  if (card.image_url) {
    if (card.image_url.startsWith("/api/")) {
      const origin = API.replace(/\/api$/, "");
      return `${origin}${card.image_url}`;
    }
    return card.image_url;
  }
  if (card.section === "resources" && IMAGES.resourcesBySlug && card.slug && IMAGES.resourcesBySlug[card.slug]) {
    return IMAGES.resourcesBySlug[card.slug];
  }
  const bucket = IMAGES[card.section];
  if (bucket && typeof bucket === "object") return bucket[card.order] || "";
  return "";
}

function absoluteUrl(card) {
  if (!card) return "";
  const img = resolveImage(card);
  if (!img) return "";
  if (/^https?:\/\//.test(img)) return img;
  // origin of the current page
  if (typeof window !== "undefined") return `${window.location.origin}${img}`;
  return img;
}

export default function CardDetail({ section }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [card, setCard] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    setNotFound(false);
    setCard(null);
    setRelated([]);

    let cancelled = false;
    api
      .get(`/cards/by-slug/${section}/${slug}`)
      .then(({ data }) => {
        if (cancelled) return;
        setCard(data);
        // Fetch related (same section, excluding this card)
        return api
          .get("/cards", { params: { section } })
          .then(({ data: list }) => {
            if (cancelled) return;
            const others = (Array.isArray(list) ? list : [])
              .filter((c) => c.id !== data.id)
              .slice(0, 3);
            setRelated(others);
          });
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) setNotFound(true);
        else setNotFound(true); // any error → 404 page (graceful)
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [section, slug]);

  if (notFound) return <NotFound />;

  if (loading || !card) {
    return (
      <div className="min-h-screen bg-white text-neutral-900 antialiased">
        <Nav />
        <main className="pt-16">
          <div
            data-testid="card-detail-skeleton"
            className="w-full h-[360px] md:h-[520px] bg-neutral-200 animate-pulse"
          />
          <div className="mx-auto max-w-[720px] px-4 md:px-6 py-16 md:py-20 grid place-items-center">
            <div className="h-8 w-8 rounded-full border-2 border-neutral-200 border-t-neutral-900 animate-spin" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const display = SECTION_DISPLAY[section] || section;
  const pluralLabel = SECTION_PLURAL[section] || `More ${section}`;
  const anchor = SECTION_ANCHOR[section] || "#top";
  const heroImg = resolveImage(card);
  const ogImg = absoluteUrl(card);
  const pageUrl =
    typeof window !== "undefined" ? `${window.location.origin}${window.location.pathname}` : "";

  const seoTitle = `${card.seo_title || card.title} — ScienceAI`;
  const seoDesc = card.seo_description || card.blurb;

  return (
    <div className="min-h-screen bg-white text-neutral-900 antialiased">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        {ogImg && <meta property="og:image" content={ogImg} />}
        {pageUrl && <link rel="canonical" href={pageUrl} />}
      </Helmet>

      <Nav />

      {/* Hero */}
      <section
        data-testid="card-detail-hero"
        className="relative w-full h-[420px] md:h-[520px] overflow-hidden bg-neutral-900"
      >
        {heroImg && (
          <img
            src={heroImg}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/55 to-black/85" />

        <div className="relative h-full mx-auto max-w-[1280px] px-4 md:px-6 flex flex-col justify-end pb-12 md:pb-16">
          <p
            data-testid="card-detail-eyebrow"
            className="text-[12px] uppercase tracking-[0.22em] font-medium text-white/80"
          >
            {display}
          </p>
          <h1
            data-testid="card-detail-title"
            className="mt-3 text-[40px] sm:text-[48px] md:text-[56px] font-semibold tracking-[-0.02em] leading-[1.05] text-white max-w-[920px]"
          >
            {card.title}
          </h1>
          <p
            data-testid="card-detail-blurb"
            className="mt-4 text-[15px] md:text-[17px] text-white/85 max-w-[680px] leading-relaxed"
          >
            {card.blurb}
          </p>

          {section === "resources" && Array.isArray(card.links) && card.links.length > 0 && (
            <div
              data-testid="card-detail-resource-links"
              className="mt-5 flex flex-wrap gap-2"
            >
              {card.links.map((l, i) => (
                <a
                  key={i}
                  href={l.url || "#"}
                  target={l.url && /^https?:\/\//.test(l.url) ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/30 bg-white/10 backdrop-blur-sm px-4 h-9 text-[12px] font-medium text-white hover:bg-white/20 transition-colors"
                >
                  {l.label || "Open"}
                  <ExternalLink className="h-3 w-3" />
                </a>
              ))}
            </div>
          )}

          <div className="mt-6">
            <Link
              to="/"
              state={{ scrollTo: anchor }}
              data-testid="card-detail-back"
              className="inline-flex items-center gap-2 rounded-full bg-white/15 border border-white/30 backdrop-blur-sm px-4 h-9 text-[12px] font-medium text-white hover:bg-white/25 transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to {display.toLowerCase()}s
            </Link>
          </div>

          <ShareRow title={card.title} className="mt-5" />
        </div>
      </section>

      {/* Body */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[720px] px-4 md:px-6">
          {card.body ? (
            <Markdown
              source={card.body}
              className="prose prose-lg max-w-none prose-headings:tracking-tight prose-headings:font-semibold prose-h2:text-[28px] prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-[20px] prose-h3:mt-8 prose-p:text-neutral-700 prose-p:leading-[1.7] prose-li:text-neutral-700 prose-a:text-[#2d5fd4] prose-a:no-underline hover:prose-a:underline prose-strong:text-neutral-900 prose-blockquote:border-l-[3px] prose-blockquote:border-neutral-300 prose-blockquote:text-neutral-600 prose-blockquote:not-italic prose-blockquote:font-normal prose-code:text-[13px] prose-code:bg-neutral-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none prose-pre:bg-neutral-900 prose-pre:text-neutral-100"
            />
          ) : (
            <p
              data-testid="card-detail-empty-body"
              className="text-[14px] text-neutral-500 italic"
            >
              Detail content coming soon.
            </p>
          )}
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section
          data-testid="card-detail-related"
          className="bg-neutral-50 py-16 md:py-24 border-t border-neutral-100"
        >
          <div className="mx-auto max-w-[1280px] px-4 md:px-6">
            <h2 className="text-[24px] md:text-[32px] font-semibold tracking-[-0.02em] text-neutral-900">
              {pluralLabel}
            </h2>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {related.map((c) => (
                <Link
                  key={c.id}
                  to={`/${c.section}/${c.slug}`}
                  data-testid={`related-card-${c.slug}`}
                  className="group flex flex-col rounded-2xl bg-white border border-neutral-200 overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_12px_32px_rgba(0,0,0,0.08)]"
                >
                  <div className="aspect-[4/3] w-full overflow-hidden bg-neutral-100">
                    <img
                      src={resolveImage(c)}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                    />
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <h3 className="text-[17px] font-semibold tracking-tight text-neutral-900 leading-snug">
                      {c.title}
                    </h3>
                    <p className="mt-2 text-[13px] text-neutral-600 leading-relaxed line-clamp-3">
                      {c.blurb}
                    </p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-medium text-neutral-900">
                      Read more
                      <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CtaBanner />
      <Footer />
    </div>
  );
}
